/** The feedback module's API types: what its endpoints answer and take. */

/** Where the person was when they wrote feedback, captured by this app when they chose to include it. */
export interface FeedbackScreen {
  area: 'operator' | 'admin' | 'worker'
  path: string
  title: string
  url: string
  viewport: string
  userAgent: string
  capturedAt: string
  /** Whatever else the screen knew about itself: an open facility, a worker, a dialog. */
  details?: Record<string, string>
}

export type FeedbackStatus = 'open' | 'completed' | 'discarded'

/** What kind of feedback it is; `general` is the default and the catch-all. */
export type FeedbackCategory = 'general' | 'feature_request' | 'bug' | 'usability' | 'data_issue' | 'performance' | 'question' | 'security'

export type FeedbackSubmitterKind = 'operator' | 'facility_administrator' | 'platform_admin'

/** Who asked, as far as the API could tell from the credential they used. */
export interface FeedbackSubmitter {
  kind: FeedbackSubmitterKind
  clientId: string | null
  organizationName: string | null
  name: string | null
  /** The name's two parts as they stood on the account. Absent from an API that predates them. */
  firstName?: string | null
  lastName?: string | null
  email: string | null
  /** A platform admin's login, kept beside their name. */
  username?: string | null
}

export interface FeedbackResolution {
  outcome: 'completed' | 'discarded'
  by: string
  /** The resolving admin's name when one was on file. */
  byName?: string | null
  at: string
  reason: string | null
}

/** `POST /feedback`, `GET /feedback`, `GET /admin/feedback`: one piece of product feedback. */
export interface FeedbackItem {
  feedbackId: string
  comment: string
  category: FeedbackCategory
  screen: FeedbackScreen | null
  submittedBy: FeedbackSubmitter
  submittedAt: string
  status: FeedbackStatus
  /** Position among open items, lowest first. */
  rank: number
  resolution: FeedbackResolution | null
  /** The change request developing it (admin list only); null or absent when none is, or the last one was rejected or cancelled. */
  changeRequestId?: string | null
}

export interface SubmitFeedbackInput {
  comment: string
  /** Defaults to general on the API. */
  category?: FeedbackCategory
  screen?: FeedbackScreen
}

export type ResolveFeedbackInput = { status: 'completed' } | { status: 'discarded'; reason: string } | { status: 'open' }

// --- Change requests: feedback becomes a change (docs/plans/feedback-to-change-plan.md, phase 0) ---

export interface AcceptanceCriterion {
  scenario: string
  /** The Cucumber section it adds to or updates; null when none is named yet. */
  cucumberSection: string | null
}

export interface PolicyDecision {
  question: string
  /** The user's answer; null while open. Submit is refused while any is open. */
  answer: string | null
}

/** What the change is to do, as an admin writes it. */
export interface Requirement {
  title: string
  problem: string
  behaviour: string
  acceptanceCriteria: AcceptanceCriterion[]
  scope: string[]
  outOfScope: string[]
  policyDecisions: PolicyDecision[]
  docsToUpdate: string[]
}

export const CHANGE_REQUEST_STATUSES = ['draft', 'proposed', 'approved', 'ready_for_review', 'accepted', 'rejected', 'cancelled'] as const
export type ChangeRequestStatus = (typeof CHANGE_REQUEST_STATUSES)[number]
export const CLOSED_CHANGE_STATUSES: readonly ChangeRequestStatus[] = ['accepted', 'rejected', 'cancelled']

/** The admin who took a step. */
export interface ChangeActor {
  username: string
  name: string | null
  role: 'root' | 'admin'
}

export interface RequirementVersion {
  version: number
  requirement: Requirement
  /** SHA-256 over the requirement's canonical JSON; the root's approval names it. */
  contentHash: string
  createdBy: ChangeActor
  createdAt: string
}

export interface ChangeApproval {
  version: number
  contentHash: string
  baseSha: string | null
  by: string
  byName: string | null
  at: string
}

export interface ChangePullRequest {
  url: string
  number: number | null
  branch: string | null
  headSha: string
  recordedBy: string
  recordedAt: string
}

export interface ChangeAcceptance {
  headSha: string
  by: string
  byName: string | null
  at: string
}

export interface ChangeClosure {
  outcome: 'rejected' | 'cancelled'
  reason: string
  by: string
  byName: string | null
  at: string
}

export type ChangeEventKind = 'created' | 'requirement_saved' | 'submitted' | 'returned' | 'approved' | 'pull_request_recorded' | 'sent_back' | 'accepted' | 'rejected' | 'cancelled'

export interface ChangeEvent {
  at: string
  actor: ChangeActor
  kind: ChangeEventKind
  from: ChangeRequestStatus | null
  to: ChangeRequestStatus
  /** The reason, notes or version the step carried. */
  note: string | null
}

export interface ChangeRequest {
  changeRequestId: string
  feedbackIds: string[]
  status: ChangeRequestStatus
  /** Append-only; the last is the current one. */
  versions: RequirementVersion[]
  approval: ChangeApproval | null
  pullRequest: ChangePullRequest | null
  acceptance: ChangeAcceptance | null
  closure: ChangeClosure | null
  events: ChangeEvent[]
  createdBy: ChangeActor
  createdAt: string
  updatedAt: string
  /** Every save is conditional on it. */
  version: number
}

/** `GET /admin/changes` and every step's answer: the record plus what its latest requirement still lacks, in words. */
export interface ChangeRequestView extends ChangeRequest {
  missingForSubmit: string[]
}

/** `GET /admin/changes/{id}/prompt` (root only): the approved version's prompt to run by hand. */
export interface ChangePrompt {
  changeRequestId: string
  branch: string
  requirementVersion: number
  contentHash: string
  prompt: string
}

export interface ApproveChangeInput {
  requirementVersion: number
  contentHash: string
  /** The commit of main the work starts from. */
  baseSha?: string
}

export interface RecordPullRequestInput {
  url: string
  headSha: string
  branch?: string
}
